import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useStyle } from "../context/StyleContext";

const PhotoCapture = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const navigate = useNavigate();
  const styles = useStyle();

  useEffect(() => {
    const getUserMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Error accessing webcam: ", err);
      }
    };

    getUserMedia();

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  const startCapture = async () => {
    setIsCapturing(true);
    for (let i = 0; i < 3; i++) {
      await countdownTimer();
      capturePhoto();
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Extra 1-second delay
    }
    setIsCapturing(false);
    navigate("/preview-confirmation");
  };

  const countdownTimer = () => {
    return new Promise((resolve) => {
      let count = 3;
      setCountdown(count);
      const timer = setInterval(() => {
        count -= 1;
        setCountdown(count);
        if (count <= 0) {
          clearInterval(timer);
          resolve();
        }
      }, 1000);
    });
  };

  const capturePhoto = () => {
    const context = canvasRef.current.getContext("2d");
    context.setTransform(-1, 0, 0, 1, canvasRef.current.width, 0); // Flip horizontally
    context.drawImage(
      videoRef.current,
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );
    context.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
    const imageSrc = canvasRef.current.toDataURL("image/jpeg");
    setPhotos((prevPhotos) => {
      const updatedPhotos = [...prevPhotos, imageSrc];
      localStorage.setItem("capturedPhotos", JSON.stringify(updatedPhotos));
      return updatedPhotos;
    });
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-gradient-to-b overflow-hidden">
      <motion.h1
        className="text-6xl font-bold text-white mb-8"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{ color: styles.textColor, fontFamily: styles.fontFamily }}
      >
        PHOTO CAPTURE
      </motion.h1>
      <motion.video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full max-w-2xl border-solid border-8 rounded-lg shadow-lg mb-4 transform -scale-x-100" // Flip the video horizontally
        style={{ borderColor: styles.borderColor }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <canvas ref={canvasRef} width="640" height="480" className="hidden" />
      {isCapturing ? (
        <motion.div
          className="text-white text-3xl font-bold mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          Capturing in {countdown}
        </motion.div>
      ) : (
        <div className="mt-4 flex space-x-4">
          {photos.length < 3 && (
            <motion.button
              onClick={startCapture}
              className="px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: styles.backgroundColor,
                fontFamily: styles.fontFamily,
              }}
            >
              <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-green-500 rounded-full group-hover:w-56 group-hover:h-56"></span>
              <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-700"></span>
              <span
                className="relative text-5xl p-5"
                style={{
                  fontFamily: styles.fontFamily,
                  color: styles.textColor,
                }}
              >
                Start Capturing
              </span>
            </motion.button>
          )}
        </div>
      )}
      <motion.div
        className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 p-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {photos.map((photo, index) => (
          <motion.img
            key={index}
            src={photo}
            alt={`Captured ${index + 1}`}
            className="w-full rounded-lg shadow-lg border-solid border-8"
            style={{ borderColor: styles.borderColor }}
            whileHover={{ scale: 1.05 }}
          />
        ))}
      </motion.div>
      {/* Removed the Confirm button */}
    </div>
  );
};

export default PhotoCapture;
